import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

interface KpiCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  tooltip?: string;
  trend?: "up" | "down" | "flat";
  trendValue?: string;
  trendPositive?: boolean; // Is the "up" direction good? (e.g., completion rate up = good, overdue up = bad)
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  icon,
  color,
  tooltip,
  trend,
  trendValue,
  trendPositive = true,
}) => {
  const getTrendColor = () => {
    if (!trend || trend === "flat") return dt.textMuted;
    const isGood = (trend === "up" && trendPositive) || (trend === "down" && !trendPositive);
    return isGood ? "#10B981" : "#EF4444";
  };

  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : TrendingFlat;

  const card = (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        p: 2.5,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            color: dt.textSecondary,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: dt.radiusSm,
            background: `${color}12`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "28px",
          fontWeight: 700,
          color: dt.textPrimary,
          lineHeight: 1.2,
          mb: 0.5,
        }}
      >
        {value}
      </Typography>

      {trend && trendValue && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TrendIcon sx={{ fontSize: 16, color: getTrendColor() }} />
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 500,
              color: getTrendColor(),
            }}
          >
            {trendValue}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        {card}
      </Tooltip>
    );
  }

  return card;
};

export default KpiCard;
